import { dateLib } from "@agentcall/core/lib"
import { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { roomApi } from "@/api/client/room"
import { tenantApplicationApi } from "@/api/client/tenant-application"
import type {
  RoomGet200RoomTenantsItem,
  TenantApplicationGet200TenantApplication,
  TenantApplicationGet200TenantApplicationFilesItem,
  TenantApplicationGet200TenantApplicationRoom,
} from "@/api/generated/models"
import { TenantApplicationUpdateStatusBodyApprovalStatus } from "@/api/generated/models/tenantApplicationUpdateStatusBodyApprovalStatus"
import { getErrorMessage } from "@/api/instance"
import { toast } from "@/hooks/use-toast"
import type { PlanStartFormData } from "../PlanStartDialog"
import type { RejectionFormData } from "../TenantApplicationRejectionDialog"

export type ApprovalFlowStep =
  | "idle"
  | "view"
  | "approve"
  | "reject"
  | "planStart"
  | "registeredTenantWarning"
  | "complete"

interface UseTenantApplicationApprovalFlowProps {
  ticketId?: string
  roomId?: number
  onComplete?: () => void
}

const hasTenantConflictWithApplicationStartDate = (
  tenant: Pick<RoomGet200RoomTenantsItem, "startDate" | "endDate">,
  applicationStartDate: string
) => {
  if (!tenant.startDate) return false

  const isActiveAtApplicationStart =
    tenant.startDate <= applicationStartDate &&
    (!tenant.endDate || tenant.endDate >= applicationStartDate)
  const isNextTenantAfterApplicationStart = tenant.startDate >= applicationStartDate

  return isActiveAtApplicationStart || isNextTenantAfterApplicationStart
}

export function useTenantApplicationApprovalFlow({
  roomId,
  onComplete,
}: UseTenantApplicationApprovalFlowProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [step, setStep] = useState<ApprovalFlowStep>("idle")
  const [tenantApplicationId, setTenantApplicationId] = useState<string | null>(null)

  // tenantApplicationApiのフックを使用してデータ取得
  const {
    data: tenantApplicationData,
    isLoading: isFetchingTenantApplication,
    isError: isTenantApplicationError,
  } = tenantApplicationApi.useTenantApplicationGet(
    tenantApplicationId ?? "0",
    {
      withRoom: "true",
      withRoomProperty: "true",
      withFiles: "true",
    },
    {
      query: {
        enabled: !!tenantApplicationId && tenantApplicationId !== "0" && step !== "idle",
        // 取得失敗は下の useEffect で表示する
        meta: { skipErrorToast: true },
      },
    }
  )

  // データを展開
  const tenantApplication: TenantApplicationGet200TenantApplication | null =
    tenantApplicationData?.tenantApplication ?? null
  const room: TenantApplicationGet200TenantApplicationRoom | null = tenantApplication?.room ?? null
  const files: TenantApplicationGet200TenantApplicationFilesItem[] = tenantApplication?.files ?? []

  const {
    data: roomData,
    isLoading: isFetchingRoom,
    isError: isRoomError,
  } = roomApi.useRoomGet(
    roomId ?? null,
    {
      withProperty: "true",
      withTenants: "true",
    },
    {
      query: {
        enabled:
          !!roomId && !!tenantApplicationId && tenantApplicationId !== "0" && step !== "idle",
        meta: { skipErrorToast: true },
      },
    }
  )

  const roomWithTenants = roomData?.room ?? null
  const hasConflictingRegisteredTenant =
    !!tenantApplication?.startDate &&
    (roomWithTenants?.tenants ?? []).some((tenant) =>
      hasTenantConflictWithApplicationStartDate(tenant, tenantApplication.startDate)
    )
  const roomDetailPath =
    roomWithTenants?.propertyId && roomWithTenants?.id
      ? `/properties/${roomWithTenants.propertyId}/rooms/${roomWithTenants.id}`
      : null

  // エラー時の処理
  useEffect(() => {
    if (isTenantApplicationError && step !== "idle") {
      toast({ title: "申請情報の取得に失敗しました", variant: "destructive" })
      setStep("idle")
    }
  }, [isTenantApplicationError, step])

  useEffect(() => {
    if (isRoomError && step !== "idle") {
      toast({ title: "部屋情報の取得に失敗しました", variant: "destructive" })
      setStep("idle")
    }
  }, [isRoomError, step])

  // API Mutations
  const updateStatusMutation = tenantApplicationApi.useTenantApplicationUpdateStatus({
    mutation: {
      onSuccess: () => {
        toast({ title: "ステータスを更新しました" })
      },
      onError: (error) => {
        toast({
          title: "ステータスの更新に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        })
      },
    },
  })

  // URLパラメータからtenantApplicationIdを検知してモーダルを自動開く
  useEffect(() => {
    const tenantApplicationIdParam = searchParams.get("tenantApplicationId")
    if (tenantApplicationIdParam) {
      setTenantApplicationId(tenantApplicationIdParam)
      setStep("view")
    }
  }, [searchParams])

  // モーダルを開く
  const openApprovalFlow = useCallback(
    (id: number) => {
      setTenantApplicationId(id.toString())
      setStep("view")
      // URLパラメータを更新
      setSearchParams((prev) => {
        prev.set("tenantApplicationId", id.toString())
        return prev
      })
    },
    [setSearchParams]
  )

  // モーダルを閉じる
  const closeApprovalFlow = useCallback(() => {
    setStep("idle")
    setTenantApplicationId(null)
    // URLパラメータを削除
    setSearchParams((prev) => {
      prev.delete("tenantApplicationId")
      return prev
    })
  }, [setSearchParams])

  // 承認ボタン押下
  const handleApprove = useCallback(() => {
    if (hasConflictingRegisteredTenant) {
      setStep("registeredTenantWarning")
      return
    }
    setStep("planStart")
  }, [hasConflictingRegisteredTenant])

  // 棄却ボタン押下
  const handleReject = useCallback(() => {
    setStep("reject")
  }, [])

  // 棄却フォーム送信
  const handleRejectionSubmit = useCallback(
    async (data: RejectionFormData) => {
      if (!tenantApplicationId) return

      try {
        await updateStatusMutation.mutateAsync({
          id: Number.parseInt(tenantApplicationId, 10),
          data: {
            approvalStatus: TenantApplicationUpdateStatusBodyApprovalStatus.rejected,
            rejectionReason: data.rejectionReason,
            needsFollowup: data.needsFollowup,
            followupRequest: data.followupRequest || undefined,
          },
        })
        setStep("complete")
        setTimeout(() => {
          closeApprovalFlow()
          onComplete?.()
        }, 1000)
      } catch {
        // エラーはミューテーションで処理される
      }
    },
    [tenantApplicationId, updateStatusMutation, closeApprovalFlow, onComplete]
  )

  // プラン開始フォーム送信
  const handlePlanStartSubmit = useCallback(
    async (data: PlanStartFormData) => {
      if (!tenantApplicationId || !roomId) return

      try {
        // 承認 API が賃借人とプラン（プラスプラン固定）を同じトランザクションで作る
        await updateStatusMutation.mutateAsync({
          id: Number.parseInt(tenantApplicationId, 10),
          data: {
            approvalStatus: TenantApplicationUpdateStatusBodyApprovalStatus.approved,
            planStartDate: dateLib.formatDateISO(data.startDate),
          },
        })

        setStep("complete")
        setTimeout(() => {
          closeApprovalFlow()
          onComplete?.()
        }, 1000)
      } catch {
        // エラーはミューテーションで処理される
      }
    },
    [tenantApplicationId, roomId, updateStatusMutation, closeApprovalFlow, onComplete]
  )

  // ステップを戻る
  const handleBack = useCallback(() => {
    switch (step) {
      case "reject":
      case "planStart":
      case "registeredTenantWarning":
        setStep("view")
        break
      default:
        closeApprovalFlow()
    }
  }, [step, closeApprovalFlow])

  return {
    // State
    step,
    tenantApplication,
    room,
    files,
    hasConflictingRegisteredTenant,
    roomDetailPath,
    loading: isFetchingTenantApplication || isFetchingRoom || updateStatusMutation.isPending,

    // Dialogs open state
    isViewDialogOpen: step === "view",
    isRejectDialogOpen: step === "reject",
    isPlanStartDialogOpen: step === "planStart",
    isRegisteredTenantWarningDialogOpen: step === "registeredTenantWarning",
    isComplete: step === "complete",

    // Actions
    openApprovalFlow,
    closeApprovalFlow,
    handleApprove,
    handleReject,
    handleRejectionSubmit,
    handlePlanStartSubmit,
    handleBack,
  }
}
